<template>
  <div class="numerical-mode-layout">
    <section class="task-panel task-launch-panel">
      <div class="panel-header task-header">
        <div class="panel-heading">
          <h3 class="panel-title">任務區</h3>
        </div>
        <button
          v-if="taskEmbedUrl"
          type="button"
          class="task-drawer-toggle"
          :class="{ 'task-drawer-toggle--attention': !hasOpenedTask }"
          @click="openTaskDrawer"
        >
          {{ hasOpenedTask ? "繼續任務／提交答案" : "接取任務" }}
        </button>
      </div>
      <div
        v-if="taskEmbedUrl"
        class="task-guidance"
        :class="{ 'task-guidance--pending': !hasOpenedTask }"
      >
        <template v-if="!hasOpenedTask">
          <strong>下一步：請先接取任務</strong>
          <span>請按右上角「接取任務」，觀看影片與閱讀題目後，再開始操作 NPC 與試算表。</span>
        </template>
        <template v-else>
          <strong>下一步：完成本關並回任務填答</strong>
          <span>完成 NPC 與試算表操作後，點選「繼續任務／提交答案」回到表單。答案正確時，表單會自動顯示下一關影片。</span>
        </template>
      </div>
    </section>

    <div
      v-if="hasMountedTaskDrawer"
      v-show="isTaskDrawerOpen"
      class="task-drawer-backdrop"
      @click.self="isTaskDrawerOpen = false"
    >
      <aside class="task-drawer" aria-label="任務區">
        <div class="panel-header task-header">
          <div class="panel-heading">
            <h3 class="panel-title">任務區</h3>
          </div>
          <button
            type="button"
            class="task-drawer-toggle"
            @click="isTaskDrawerOpen = false"
          >
            關閉
          </button>
        </div>

        <div class="task-content">
          <div class="task-frame-wrap">
            <iframe :src="taskEmbedUrl" class="task-frame" allowfullscreen></iframe>
          </div>
        </div>
      </aside>
    </div>

    <section class="bottom-row">
      <section class="chat-panel">
        <div class="panel-header">
          <div class="panel-heading">
            <h3 class="panel-title">問答區</h3>
          </div>
        </div>

        <div v-if="$slots['chat-panel']" class="chat-panel-slot">
          <slot name="chat-panel"></slot>
        </div>

        <template v-else>
        <div class="chat-log-box">
          <div class="changes-title">聊天室紀錄</div>
          <div v-if="chatLog.length === 0" class="chat-placeholder">
            目前尚無對話紀錄。
          </div>
          <div v-else class="chat-list">
            <div
              v-for="(item, index) in chatLog"
              :key="`${index}-${item.role}-${item.content}`"
              class="chat-item"
            >
              <div class="chat-role">{{ item.userName || item.role || "system" }}</div>
              <div class="chat-content">{{ item.content }}</div>
            </div>
          </div>

          <div v-if="waitAnimation" class="chat-status-box">
            <strong>{{ npcName || "NPC" }}</strong>
            <span>思考回應中...</span>
          </div>
        </div>

        <div class="chat-input-bar">
          <textarea
            :value="chatContent"
            class="chat-input"
            rows="1"
            :disabled="isAiMessageLocked || endGame"
            :placeholder="endGame ? '遊戲已結束' : '請輸入要送給 NPC 的訊息...'
            "
            @input="handleInput"
            @focus="$emit('resize-chat-input', $event)"
            @keydown.enter="$emit('send-message', $event)"
            @compositionstart="$emit('composition-start')"
            @compositionend="$emit('composition-end')"
          ></textarea>
          <button
            class="chat-send-btn"
            :disabled="isAiMessageLocked || endGame"
            @click="$emit('send-message')"
          >
            送出給 {{ npcName || 'NPC' }}
          </button>
        </div>
        </template>
      </section>

      <section class="sheet-panel">
        <div class="panel-header sheet-panel-header">
          <div class="panel-heading">
            <h3 class="panel-title">工作區</h3>
          </div>
          <a
            v-if="showConfigControls && (sheetLinkUrl || sheetUrl)"
            :href="sheetLinkUrl || sheetUrl"
            class="sheet-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            新分頁開啟
          </a>
        </div>

        <div v-if="sheetUrl" class="sheet-frame-wrap">
          <iframe :src="sheetUrl" class="sheet-frame" allowfullscreen></iframe>
        </div>
        <div v-else class="sheet-placeholder">
          {{
            showConfigControls
              ? "請提供 sheetId 或 sheetUrl，才能顯示右側試算表。"
              : "目前尚未提供試算表。"
          }}
        </div>

        <div class="sheet-floating-bar">
          <!--
            正式版：保留手動輸入 Google Sheet ID 的能力。
            若網址已固定帶入 sheetId，則改用唯讀顯示，避免使用者重複輸入。
          -->
          <label v-if="showConfigControls && !sheetIdLocked" class="field-label">
            Google Sheet ID
            <input
              :value="sheetId"
              type="text"
              class="field-input"
              placeholder="輸入 Google Sheet ID"
              @input="$emit('update:sheetId', $event.target.value)"
            />
          </label>
          <div v-else-if="showConfigControls" class="field-label field-readonly-box">
            <span>Google Sheet ID</span>
            <div class="field-readonly-value">{{ sheetId }}</div>
            <small class="field-readonly-help">此欄位已由數值模式網址固定帶入。</small>
          </div>
          <!--
            正式版若需要限定讀取範圍，可恢復 Range 欄位。
            目前數值模式預設讀整張表，所以先保留註解，不在 UI 顯示。
          <label class="field-label">
            Range
            <input
              :value="sheetRange"
              type="text"
              class="field-input"
              placeholder="例如 Sheet1!A1:D20"
              @input="$emit('update:sheetRange', $event.target.value)"
            />
          </label>
          -->
          <button
            class="submit-btn"
            :disabled="isLoadingSheet || isSheetSubmitDisabled"
            @click="$emit('submit-sheet')"
          >
            {{ isLoadingSheet ? "讀取中..." : "送出本回合配置" }}
          </button>
        </div>
        <div
          v-if="sheetStatusMessage"
          class="sheet-status-message"
          :class="{
            'sheet-status-message--error':
              isSheetSubmitDisabled && !isLoadingSheet,
          }"
        >
          {{ sheetStatusMessage }}
        </div>

        <div
          v-if="showConfigControls && sheetChanges.length > 0"
          class="changes-box"
        >
          <div class="changes-title">最近偵測到的欄位變更</div>
          <div
            v-for="change in sheetChanges"
            :key="`${change.a1Notation}-${change.triggeredAt || change.editedAt || change.newValue}`"
            class="change-item"
          >
            <strong>{{ change.a1Notation }}</strong>
            <span>{{ change.oldValue }} -> {{ change.newValue }}</span>
          </div>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
export default {
  name: "NumericalNPC",
  data() {
    return {
      isTaskDrawerOpen: false,
      hasOpenedTask: false,
      hasMountedTaskDrawer: false,
    };
  },
  props: {
    showConfigControls: {
      type: Boolean,
      default: false,
    },
    sheetUrl: {
      type: String,
      default: "",
    },
    sheetLinkUrl: {
      type: String,
      default: "",
    },
    sheetId: {
      type: String,
      default: "",
    },
    sheetIdLocked: {
      type: Boolean,
      default: false,
    },
    sheetRange: {
      type: String,
      default: "",
    },
    taskDescription: {
      type: String,
      default: "",
    },
    taskEmbedUrl: {
      type: String,
      default: "",
    },
    npcName: {
      type: String,
      default: "",
    },
    userName: {
      type: String,
      default: "",
    },
    roomCode: {
      type: String,
      default: "",
    },
    chatLog: {
      type: Array,
      default: () => [],
    },
    sheetChanges: {
      type: Array,
      default: () => [],
    },
    chatContent: {
      type: String,
      default: "",
    },
    isLoadingSheet: {
      type: Boolean,
      default: false,
    },
    isSheetSubmitDisabled: {
      type: Boolean,
      default: false,
    },
    sheetStatusMessage: {
      type: String,
      default: "",
    },
    isAiMessageLocked: {
      type: Boolean,
      default: false,
    },
    waitAnimation: {
      type: Boolean,
      default: false,
    },
    endGame: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    handleInput(event) {
      this.$emit('update:chatContent', event.target.value);
      this.$emit('resize-chat-input', event);
    },
    logSheetDebugInfo() {
      console.info('[NumericalNPC] sheet debug', {
        sheetId: this.sheetId || '',
        sheetUrl: this.sheetUrl || '',
        sheetLinkUrl: this.sheetLinkUrl || '',
      });
    },
    openTaskDrawer() {
      this.hasOpenedTask = true;
      this.hasMountedTaskDrawer = true;
      this.isTaskDrawerOpen = true;
    },
  },
  mounted() {
    this.logSheetDebugInfo();
  },
  watch: {
    sheetUrl() {
      this.logSheetDebugInfo();
    },
    sheetId() {
      this.logSheetDebugInfo();
    },
  },
};
</script>

<style scoped>
.numerical-mode-layout {
  display: grid;
  grid-template-rows: auto auto;
  gap: 16px;
  min-height: 100vh;
  padding-bottom: 24px;
  overflow: visible;
  align-items: start;
}

.task-panel,
.chat-panel,
.sheet-panel {
  background: #ffffff;
  border: 1px solid #dfe6ee;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.task-panel {
  min-height: 0;
}

.task-launch-panel {
  min-height: 72px;
}

.task-guidance {
  display: grid;
  gap: 4px;
  padding: 0 18px 16px;
  text-align: left;
  color: #486581;
  font-size: 14px;
}

.task-guidance strong {
  color: #1f6feb;
  font-size: 16px;
}

.task-guidance--pending {
  margin: 0 14px 14px;
  padding: 16px 18px;
  border: 2px solid #f59e0b;
  border-radius: 12px;
  background: #fffbeb;
  box-shadow: 0 6px 18px rgba(245, 158, 11, 0.18);
}

.task-guidance--pending strong {
  color: #92400e;
  font-size: 19px;
}

.task-guidance--pending span {
  color: #78350f;
  line-height: 1.55;
}


.task-drawer-toggle--attention {
  animation: task-drawer-pulse 1.8s ease-in-out infinite;
}

@keyframes task-drawer-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(31, 111, 235, 0.35);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 0 8px rgba(31, 111, 235, 0);
  }
}

.task-drawer-toggle {
  border: 1px solid #1f6feb;
  border-radius: 8px;
  padding: 8px 12px;
  color: #ffffff;
  background: #1f6feb;
  font-size: 14px;
  cursor: pointer;
}

.task-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.4);
}

.task-drawer {
  display: flex;
  flex-direction: column;
  width: min(720px, 100vw);
  height: 100dvh;
  background: #ffffff;
  box-shadow: -12px 0 30px rgba(15, 23, 42, 0.2);
}

.task-drawer .task-content {
  flex: 1;
  overflow: hidden;
}

.task-drawer .task-frame-wrap,
.task-drawer .task-frame {
  height: 100%;
  min-height: 0;
}

.bottom-row {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 16px;
  align-items: stretch;
}

.chat-panel,
.sheet-panel {
  width: 100%;
}

.chat-panel {
  min-height: clamp(520px, 70vh, 960px);
  height: clamp(520px, 70vh, 960px);
}

.sheet-panel {
  min-height: clamp(520px, 70vh, 960px);
  height: clamp(520px, 70vh, 960px);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #edf2f7;
  position: relative;
}

.panel-heading {
  flex: 1;
  text-align: center;
}

.panel-kicker {
  margin: 0 0 2px;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #829ab1;
  font-weight: 700;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  color: #243b53;
  line-height: 1.2;
}

.panel-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7c93;
  line-height: 1.35;
}

.task-content {
  flex: 1;
  display: flex;
  padding: 16px 18px 18px;
  min-height: 0;
  overflow: visible;
}

.task-frame-wrap {
  flex: 1;
  min-height: clamp(440px, 76vh, 900px);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #dbe7f3;
  background: #f8fbff;
}

.task-frame {
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  background: #ffffff;
}

.task-placeholder,
.sheet-placeholder,
.chat-placeholder {
  padding: 24px 18px;
  color: #6b7c93;
}

.task-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border-radius: 12px;
  border: 1px dashed #cbd2d9;
  background: #f8fbff;
}

.sheet-link {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #1f6feb;
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
}

.sheet-frame-wrap {
  flex: 1;
  min-height: 420px;
  overflow: hidden;
}

.sheet-frame {
  width: 100%;
  height: 100%;
  min-height: 520px;
  border: 0;
}

.chat-panel {
  min-height: 0;
}

.chat-panel-slot {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.task-floating-bar {
  display: grid;
  gap: 12px;
  padding: 14px 18px 18px;
  border-top: 1px solid #edf2f7;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, #ffffff 35%);
  backdrop-filter: blur(8px);
}

.sheet-floating-bar {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  padding: 14px 18px 18px;
  border-top: 1px solid #edf2f7;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, #ffffff 35%);
  backdrop-filter: blur(8px);
}

.field-label {
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: #486581;
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd2d9;
  border-radius: 8px;
  font-size: 14px;
}

.field-readonly-box {
  padding: 12px;
  border: 1px dashed #cbd2d9;
  border-radius: 10px;
  background: #f8fbff;
}

.field-readonly-value {
  font-size: 14px;
  color: #243b53;
  word-break: break-all;
}

.field-readonly-help {
  color: #6b7c93;
}

.submit-btn {
  border: 0;
  border-radius: 14px;
  padding: 14px 22px;
  background: #1f6feb;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 16px 32px rgba(31, 111, 235, 0.22);
}

@media (max-width: 1100px) {
  .numerical-mode-layout {
    height: auto;
    min-height: auto;
    overflow: visible;
  }

  .bottom-row {
    grid-template-columns: 1fr;
  }

  .task-panel,
  .chat-panel,
  .sheet-panel {
    min-height: 520px;
  }

  .task-panel {
    min-height: 420px;
  }

  .task-launch-panel {
    min-height: 72px;
  }

  .sheet-frame {
    min-height: 460px;
  }
}

.submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.sheet-status-message {
  padding: 0 18px 14px;
  color: #2f6b3d;
  font-size: 0.9rem;
}

.sheet-status-message--error {
  color: #b42318;
}

.changes-box,
.chat-log-box {
  padding: 16px 18px;
}

.changes-box {
  max-height: 180px;
  overflow: auto;
}

.changes-title {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #243b53;
}

.change-item {
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: #486581;
  padding: 6px 0;
}

.chat-log-box {
  flex: 1;
  overflow: auto;
}

.chat-status-box {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eef6ff;
  color: #36506c;
  display: flex;
  gap: 8px;
  align-items: center;
}

.chat-list {
  display: grid;
  gap: 10px;
}

.chat-item {
  padding: 12px;
  border-radius: 10px;
  background: #f8fbff;
  border: 1px solid #e4ecf5;
}

.chat-role {
  font-size: 12px;
  font-weight: 700;
  color: #1f6feb;
  margin-bottom: 6px;
}

.chat-content {
  font-size: 14px;
  color: #243b53;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-input-bar {
  display: grid;
  gap: 10px;
  padding: 16px 18px 18px;
  border-top: 1px solid #edf2f7;
  background: #fbfdff;
}

.chat-input {
  width: 100%;
  min-height: 44px;
  max-height: 120px;
  resize: vertical;
  border: 1px solid #cbd2d9;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  outline: none;
}

.chat-send-btn {
  border: 0;
  border-radius: 10px;
  padding: 12px 14px;
  background: #0f766e;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
}

.chat-send-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (max-width: 1100px) {
  .task-frame {
    min-height: 460px;
  }

  .task-frame-wrap {
    min-height: 460px;
  }

  .sheet-floating-bar {
    grid-template-columns: 1fr;
  }

  .sheet-frame-wrap,
  .sheet-frame,
  .chat-panel {
    min-height: 520px;
  }
}
</style>
